import moment from 'moment'
import React, {useCallback, useMemo, useRef, useState} from 'react'
import {Dropdown, Modal} from 'react-bootstrap'
import {KTSVG} from '../../../../_metronic/helpers'
import useSchedule from '../../../api/hooks/useSchedule'
import useScheduleForm from '../../../api/hooks/useScheduleForm'
import useUpdate from '../../../api/hooks/useUpdate'
import PageBuilder from '../../../pages/PageBuilder'
import ComponentFilters from '../../components/ComponentFilters'
import SplashScreen from '../../helpers/SplashScreen'
import {useFilter} from '../helpers'
import {useIntl} from 'react-intl'
import setModalIndex from '../../helpers/setModalIndex'
import {TApiSchedule} from '../../../types/api'
import {
    TModuleSchedule,
    TModuleScheduleCell,
    TModuleScheduleComponentCell,
    TModuleScheduleComponentColumn,
    TModuleScheduleDateColumn,
    TModuleScheduleModal,
    TModuleScheduleScrollButtons,
    TModuleScheduleServerCell,
    TModuleScheduleStepsColumn,
    TModuleScheduleTable,
} from './_types'
import useModalSettings from '../../helpers/useModalSettings'
import {ModalContext} from '../helpers/ModalContext'
import valuesCleaner from '../../helpers/valuesCleaner'
import usePage from '../../../api/hooks/usePage'

const getEmployeeSchedule = (
    schedule: Array<TModuleScheduleServerCell>,
    steps_list: Array<string>
) => {
    return schedule.reduce<Array<TModuleScheduleCell>>((acc, graph) => {
        const [start, end] = graph.steps
        const currentSteps = steps_list.slice(start, end + 1)

        switch (graph.status) {
            case 'busy':
                return acc.concat({
                    type: 'busy',
                    time: currentSteps[0],
                    event: graph.event,
                    cell_height: end - start + 1,
                })
            case 'available':
                return acc.concat(
                    currentSteps.map((step) => ({
                        type: 'available',
                        time: step,
                        cell_height: 1,
                        initials: graph.initials,
                    }))
                )
            default:
                return acc.concat(
                    currentSteps.map((step) => ({
                        type: 'disabled',
                        time: step,
                        cell_height: 1,
                    }))
                )
        }
    }, [])
}

const compareCabinets = (a?: string, b?: string) => {
    if (!a && !b) return 0;
    if (!a) return 1;
    if (!b) return -1;

    const getCategory = (val: string) => isNaN(Number(val)) ? 1 : 0;

    const catA = getCategory(a);
    const catB = getCategory(b);

    if (catA !== catB) return catA - catB;

    if (catA === 0) return Number(a) - Number(b);

    return a.localeCompare(b, 'ru', { sensitivity: 'base' });
};

const extractSurname = (title?: string) => {
    if (!title) return ''

    const cleared = title.replace(/\[.*?\]/g, '').trim()

    return cleared.split(/\s+/)[0]
}


const getScheduleAsArray = (data?: TApiSchedule, sortType: 'fio' | 'cabinet' = 'fio', isSortEnabled = false) => {
    if (!data || !data.schedule) return [];

    const { schedule, steps_list } = data;

    return Object.entries(schedule).map(([header, body]) => {
        if (!body) return { header, body: [] };

        const columns = Object.values(body).flatMap(employee => {
            if (Array.isArray(employee)) {
                return employee.map(e => ({
                    ...e,
                    schedule: getEmployeeSchedule(e.schedule, steps_list),
                }));
            } else {
                return [{
                    ...employee,
                    schedule: getEmployeeSchedule(employee.schedule, steps_list),
                }];
            }
        });

        if (isSortEnabled) {
            if (sortType === 'fio') {
                columns.sort((a, b) => {
                    const surnameA = extractSurname(a.performer_title);
                    const surnameB = extractSurname(b.performer_title);

                    const bySurname = surnameA.localeCompare(surnameB, 'ru', { sensitivity: 'base' });
                    if (bySurname !== 0) return bySurname;

                    return compareCabinets(a.cabinet_title, b.cabinet_title);
                });
            } else if (sortType === 'cabinet') {
                columns.sort((a, b) => {
                    const cabCompare = compareCabinets(a.cabinet_title, b.cabinet_title);
                    if (cabCompare !== 0) return cabCompare;
                    const surnameA = extractSurname(a.performer_title);
                    const surnameB = extractSurname(b.performer_title);
                    return surnameA.localeCompare(surnameB, 'ru', { sensitivity: 'base' });
                });

                return { header, body: columns };
            }
        }

        return { header, body: columns };
    });
};

const extractModalPage = (href?: string): string | null => {
    if (!href) return null
    const idx = href.indexOf('?modal=')
    if (idx === -1) return null
    return decodeURIComponent(href.slice(idx + 7)).replace(/^\//, '')
}

const ModuleScheduleCell: React.FC<TModuleScheduleComponentCell> = (props) => {
    const {type, date, time, cell_height, event, initials, innerInitials, setSelectedCell} = props
    const currentCellHeight = `${3.15 * cell_height + 0.5 * (cell_height - 1)}rem`
    const resolvedIcons = useMemo(() => {
        if (event && Array.isArray(event.icons)) {
            const iconsPerSide = 3
            const icons = event.icons
            const iconsCount = icons.length

            const rightIcons =
                iconsCount > iconsPerSide
                    ? icons.slice(iconsCount - iconsPerSide, iconsCount)
                    : icons.slice(0, iconsPerSide)
            const leftIcons =
                iconsCount > rightIcons.length ? icons.slice(0, iconsCount - iconsPerSide) : []
            return {
                left: leftIcons,
                right: rightIcons,
            }
        } else {
            return {
                left: [],
                right: [],
            }
        }
    }, [event])

    const handleClick = () => {
        if (type === 'disabled') {
            return
        } else {
            return setSelectedCell({
                type,
                date,
                time,
                event,
                initials: Object.assign({}, initials, innerInitials ?? {}),
            })
        }
    }
    if (type === 'busy') {
        if (event) {
            return (
                <div className='moduleSchedule_cellContainer' onClick={handleClick}>
                    <div className='card card-custom overlay cell-overlay'>
                        <div className='card-body p-0'>
                            <div className='overlay-wrapper moduleSchedule_cellWrapper'>
                                <div
                                    className={`moduleSchedule_cell ${type} ${event?.color}`}
                                    style={{height: currentCellHeight}}
                                >
                                    <div
                                        className={`moduleSchedule_cellContentContainer ${
                                            cell_height === 1 ? ' minimize' : ''
                                        }`}
                                    >
                                        <div className='moduleSchedule_cellIcons left'>
                                            {resolvedIcons.left.map((icon: string) => (
                                                <KTSVG
                                                    key={icon}
                                                    path={`/media/crm/icons/${icon}.svg`}
                                                    className='moduleSchedule_cellIcon'
                                                />
                                            ))}
                                        </div>
                                        {Array.isArray(event.description)
                                            ? event.description.map((descriptionRow: string, index: number) => {
                                                // Лейбл будет отображаться в строке с временем записи
                                                const TIME_ROW_INDEX = 1
                                                const isTimeRow = index === TIME_ROW_INDEX

                                                return (
                                                    <div
                                                        key={descriptionRow}
                                                        className='moduleSchedule_cellContent'
                                                    >
                                                        {isTimeRow && event?.has_dms_group && (
                                                            <span className='moduleSchedule_dmsLabel'>
                                                                ДМС
                                                            </span>
                                                        )}
                                                        <span className='moduleSchedule_cellValue'>
                                                            {descriptionRow}
                                                        </span>
                                                    </div>
                                                )
                                            })
                                            : null}
                                        <div className='moduleSchedule_cellIcons right'>
                                            {resolvedIcons.right.map((icon: string) => (
                                                <KTSVG
                                                    key={icon}
                                                    path={`/media/crm/icons/${icon}.svg`}
                                                    className='moduleSchedule_cellIcon'
                                                />
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className='overlay-layer moduleSchedule_cellOverlay'>
                                <div
                                    className={`moduleSchedule_cell ${type} ${event?.color}`}
                                    style={{height: currentCellHeight}}
                                >
                                    <div className='moduleSchedule_cellContentContainer'>
                                        {Array.isArray(event.details)
                                            ? event.details.map(
                                                  (detailRow: {icon: string; value: string}) => (
                                                      <div
                                                          key={detailRow.value}
                                                          className='moduleSchedule_cellContent'
                                                      >
                                                          <span className='moduleSchedule_cellValue'>
                                                              {detailRow.value}
                                                          </span>
                                                      </div>
                                                  )
                                              )
                                            : null}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )
        } else {
            return (
                <div className='moduleSchedule_cellContainer'>
                    <div className='card card-custom'>
                        <div className='card-body p-0'>
                            <div className='overlay-wrapper moduleSchedule_cellWrapper'>
                                <div
                                    className='moduleSchedule_cell busy danger'
                                    style={{height: currentCellHeight}}
                                ></div>
                            </div>
                        </div>
                    </div>
                </div>
            )
        }
    }
    return (
        <div className='moduleSchedule_cellContainer' onClick={handleClick}>
            <div className={`moduleSchedule_cell ${type}`} style={{height: currentCellHeight}} />
        </div>
    )
}
const ModuleScheduleColumn: React.FC<TModuleScheduleComponentColumn> = (props) => {
    const {
        performer_title,
        performer_href,
        performer_href_type,
        schedule,
        date,
        initials,
        setSelectedCell,
        openPerformerModal,
    } = props

    const modalPage = useMemo(() => {
        if (performer_href_type === 'modal') {
            return extractModalPage(performer_href) || performer_href?.replace(/^\//, '')
        }
        return extractModalPage(performer_href)
    }, [performer_href, performer_href_type])

    const isModalLink = Boolean(modalPage)
    const anchorTarget = isModalLink ? undefined : performer_href_type ?? '_blank'

    const handleHeaderClick = (e: React.MouseEvent) => {
        if (isModalLink && modalPage) {
            e.preventDefault()
            openPerformerModal(modalPage)
        }
    }

    return (
        <div className='moduleSchedule_employeeColumn'>
            <a
                href={performer_href ?? ''}
                target={anchorTarget}
                rel={anchorTarget === '_blank' ? 'noopener noreferrer' : undefined}
                onClick={handleHeaderClick}
            >
                <div className='moduleSchedule_employeeColumnHeader'>
                    <span className='moduleSchedule_employeeColumnTitle'>{performer_title}</span>
                </div>
            </a>

            {schedule.map((cell) => (
                <ModuleScheduleCell
                    key={`${cell.time}-${cell.type}`}
                    type={cell.type}
                    date={date}
                    time={cell.time}
                    cell_height={cell.cell_height}
                    event={cell.event}
                    initials={initials}
                    innerInitials={cell.initials}
                    setSelectedCell={setSelectedCell}
                />
            ))}
        </div>
    )
}

const ModuleScheduleSortButtons: React.FC<{
    sortType: 'fio' | 'cabinet'
    onChangeSort: (type: 'fio' | 'cabinet') => void
    isSortEnabled?: boolean
}> = ({ sortType, onChangeSort, isSortEnabled }) => {
    if (!isSortEnabled) return null

    return (
        <div className="moduleSchedule_sortWrapper">
            <div className="moduleSchedule_sortLabel">Сортировка по:</div>
            <div className="moduleSchedule_sortTabs">
                <button
                    className={`moduleSchedule_sortTab ${sortType === 'fio' ? 'is-active' : ''}`}
                    onClick={() => onChangeSort('fio')}
                >
                    ФИО специалиста
                </button>
                <button
                    className={`moduleSchedule_sortTab ${sortType === 'cabinet' ? 'is-active' : ''}`}
                    onClick={() => onChangeSort('cabinet')}
                >
                    Кабинету
                </button>
            </div>
        </div>

    )
}


const ModuleScheduleDateColumn: React.FC<TModuleScheduleDateColumn>  = ({
    date,
    body,
    setSelectedCell,
    openPerformerModal
}) => {
    const dayAsWord =
        moment(date).format('dddd').toLocaleUpperCase().slice(0, 1) +
        moment(date).format('dddd').toLowerCase().slice(1)
    const dayAsDate = moment(date).format('DD MMMM')
    const currentFormatDate = `${dayAsWord}, ${dayAsDate}`
    return (
        <div className='moduleSchedule_dateColumn'>
            <div className='moduleSchedule_dateColumnHeader'>
                <span className='moduleSchedule_dateColumnContent'>{currentFormatDate}</span>
            </div>
            <div className='moduleSchedule_dateColumnBody'>
                {body.map((item) => (
                    <ModuleScheduleColumn
                        key={item.performer_title + item.performer_href}
                        date={date}
                        performer_title={item.performer_title}
                        schedule={item.schedule}
                        performer_href={item.performer_href}
                        performer_href_type={item?.performer_href_type}
                        initials={item.initials}
                        setSelectedCell={setSelectedCell}
                        openPerformerModal={openPerformerModal}
                    />
                ))}
            </div>
        </div>
    )
}
const ModuleScheduleStepsColumn: React.FC<TModuleScheduleStepsColumn> = ({steps, setFilter}) => {
    const intl = useIntl()
    const [selectedStep, setSelectedStep] = useState(60)

    const stepsForSelect = useMemo(() => {
        return [5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55, 60]
    }, [])

    const setStep = useCallback((step: number) => {
        setFilter((previousFilter: any) => {
            return {
                ...previousFilter,
                step,
            }
        })
        setSelectedStep(step)
    }, [])

    return (
        <div className='moduleSchedule_steps'>
            <Dropdown className='moduleSchedule_stepsDropdown'>
                <Dropdown.Toggle id='moduleSchedule_stepsButton'>
                    {intl.formatMessage({id: 'SCHEDULE.STEPS_TITLE'})}
                </Dropdown.Toggle>

                <Dropdown.Menu>
                    {stepsForSelect.map((stepForSelect) => (
                        <Dropdown.Item
                            key={stepForSelect}
                            className={`moduleSchedule_filterStep${
                                selectedStep === stepForSelect ? ' selected' : ''
                            }`}
                            onClick={() => setStep(stepForSelect)}
                        >
                            {stepForSelect}
                        </Dropdown.Item>
                    ))}
                </Dropdown.Menu>
            </Dropdown>
            {steps.map((step) => (
                <div key={step} className='moduleSchedule_stepsCell'>
                    {step}
                </div>
            ))}
        </div>
    )
}
const ModuleScheduleTable = React.memo<TModuleScheduleTable>((props) => {
    const {steps, schedule, tableRef, setSelectedCell, setFilter, openPerformerModal} = props
    return (
        <div className='moduleSchedule_table' ref={tableRef}>
            <div className='moduleSchedule_columns'>
                <ModuleScheduleStepsColumn steps={steps} setFilter={setFilter} />
                {schedule.map((day) => (
                    <ModuleScheduleDateColumn
                        key={day.header}
                        date={day.header}
                        body={day.body}
                        setSelectedCell={setSelectedCell}
                        openPerformerModal={openPerformerModal}
                    />
                ))}
            </div>
        </div>
    )
})
const ModuleScheduleScrollButtons = React.memo<TModuleScheduleScrollButtons>(({scrollTable}) => {
    return (
        <div className='moduleSchedule_scrollButtons'>
            <div className='moduleSchedule_scrollButtonContainer left'>
                <button
                    className='moduleSchedule_scrollButton'
                    type='button'
                    onClick={() => scrollTable(-1)}
                >
                    <KTSVG path='/media/icons/duotune/arrows/arr022.svg' />
                </button>
            </div>
            <div className='moduleSchedule_scrollButtonContainer right'>
                <button
                    className='moduleSchedule_scrollButton'
                    type='button'
                    onClick={() => scrollTable(1)}
                >
                    <KTSVG path='/media/icons/duotune/arrows/arr023.svg' />
                </button>
            </div>
        </div>
    )
})

const ModulePerformerModal: React.FC<{page?: string | null; onClose: () => void}> = ({
    page,
    onClose,
}) => {
    const {data, isFetching} = usePage({page})
    const {handleEntered, handleExited} = useModalSettings(page ?? '')

    return (
        <Modal
            size='xl'
            dialogClassName='customModal moduleSchedule_modal'
            show={Boolean(page) && Boolean(data?.modules)}
            onHide={onClose}
            onEntering={setModalIndex}
            onEntered={handleEntered}
            onExited={handleExited}
        >
            <Modal.Header closeButton className='modal-emptyHeader' />
            <Modal.Body className='scroll-y'>
                <ModalContext.Provider value={{insideModal: true, setShow: onClose}}>
                    {data?.modules ? (
                        <PageBuilder
                            data={data?.modules}
                            isFetching={isFetching}
                            showProgressBar={false}
                        />
                    ) : null}
                </ModalContext.Provider>
            </Modal.Body>
        </Modal>
    )
}

const ModuleScheduleModalForm = React.memo<TModuleScheduleModal>(
    ({requestObject, selectedCell, setSelectedCell}) => {
        const {data, isFetching, refetch} = useScheduleForm(requestObject, selectedCell)

        useUpdate({
            functions: [{active: true, update: refetch}],
            table_name: requestObject,
            refetchInterval: 2000,
            enabled: Boolean(selectedCell?.event?.id),
            additionals: {
                id: selectedCell?.event?.id,
            },
        })

        const page = `${requestObject}${
            selectedCell?.event?.id ? `/update/${selectedCell.event.id}` : '/add'
        }`
        const {handleEntered, handleExited} = useModalSettings(page)

        const modalContext = useMemo(() => {
            return Object.assign(
                {setShow: setSelectedCell, refetchPage: refetch},
                selectedCell
                    ? {
                          initialData: {
                              id: selectedCell.event?.id ?? undefined,
                              start_at: `${selectedCell.date} ${selectedCell.time}:00`,
                              end_at: `${selectedCell.date} ${selectedCell.time}:00`,
                              ...selectedCell.initials,
                          },
                          insideModal: true,
                      }
                    : {}
            )
        }, [selectedCell])
        /* return <ComponentDrawer
        show={Boolean(selectedCell) && Boolean(data)}
        setShow={() => setSelectedCell(null)}
        onEntered={handleEntered}
        onExited={handleExited}
 
    >
        <ModalContext.Provider value={modalContext}>
            {data ? <PageBuilder data={data} isFetching={isFetching} showProgressBar={false} /> : null}
        </ModalContext.Provider>
    </ComponentDrawer> */

        return (
            <Modal
                size='xl'
                dialogClassName='customModal moduleSchedule_modal'
                show={Boolean(selectedCell) && Boolean(data)}
                onHide={() => setSelectedCell(null)}
                onEntering={setModalIndex}
                onEntered={handleEntered}
                onExited={handleExited}
            >
                <Modal.Header closeButton className='modal-emptyHeader' />
                <Modal.Body className='scroll-y'>
                    <ModalContext.Provider value={modalContext}>
                        {data ? (
                            <PageBuilder
                                data={data}
                                isFetching={isFetching}
                                showProgressBar={false}
                            />
                        ) : null}
                    </ModalContext.Provider>
                </Modal.Body>
            </Modal>
        )
    }
)
const ModuleSchedule: React.FC<TModuleSchedule> = (props) => {
    const {components, settings} = props
    const {object, filters} = settings
    const {filter, isInitials, setFilter, resetFilter} = useFilter(
        `${props.type}_${settings.object}`,
        filters,
        '',
        ['start_at']
    )
    const [selectedCell, setSelectedCell] = useState<any>(null)
    const [performerModal, setPerformerModal] = useState<string | null>(null)
    const openPerformerModal = useCallback((page: string) => setPerformerModal(page), [])
    const closePerformerModal = useCallback(() => setPerformerModal(null), [])

    const haveFilter = Boolean(components?.filters)

    // Сортировка: по специалисту (ФИО), по кабинету
    const SORT_KEY = 'schedule_sort_type'
    const isSortEnabled = Boolean(settings.is_sort)
    const [sortType, setSortType] = useState<'fio' | 'cabinet'>(() => {
        if (!isSortEnabled) return 'fio'
        return (localStorage.getItem(SORT_KEY) as 'fio' | 'cabinet') || 'fio'
    })
    const handleChangeSort = useCallback((type: 'fio' | 'cabinet') => {
        if (!isSortEnabled) return
        setSortType(type)
        localStorage.setItem(SORT_KEY, type)
    }, [isSortEnabled])

    const {
        data,
        isLoading: loading,
        isFetching,
        refetch,
    } = useSchedule(object, valuesCleaner(filter))
    const schedule = useMemo(() => getScheduleAsArray(data, sortType, isSortEnabled), [data, sortType, isSortEnabled])

    useUpdate({
        functions: [{active: true, update: refetch}],
        table_name: 'schedule',
        refetchInterval: 1000,
    })

    const tableRef = useRef<HTMLDivElement | null>(null)

    const scrollTable = useCallback(
        (direction = 1) => {
            if (tableRef.current) {
                const currentTableXPosition =
                    tableRef.current.scrollLeft + (tableRef.current.clientWidth / 2) * direction
                tableRef.current.scrollTo({
                    left: currentTableXPosition,
                    behavior: 'smooth',
                })
            }
        },
        [tableRef]
    )

    return (
        <>
            {haveFilter ? (
                <ComponentFilters
                    type='string'
                    data={components.filters}
                    filterValues={filter}
                    isInitials={isInitials}
                    handleChange={setFilter}
                    handleReset={resetFilter}
                    custom_format
                />
            ) : null}

            {isSortEnabled && schedule.length > 0 && (
                <ModuleScheduleSortButtons
                    sortType={sortType}
                    onChangeSort={handleChangeSort}
                    isSortEnabled={isSortEnabled}
                />
            )}

            <div className={`moduleSchedule card${isFetching ? ' loading' : ''}`}>
                <ModuleScheduleScrollButtons
                    scrollTable={scrollTable}
                />
                <ModuleScheduleTable
                    steps={data?.steps_list ?? []}
                    schedule={schedule}
                    tableRef={tableRef}
                    setSelectedCell={setSelectedCell}
                    setFilter={setFilter}
                    openPerformerModal={openPerformerModal}
                />
                <SplashScreen active={loading} />
            </div>
            <ModuleScheduleModalForm
                requestObject={object}
                selectedCell={selectedCell}
                setSelectedCell={setSelectedCell}
            />
            <ModulePerformerModal page={performerModal} onClose={closePerformerModal} />
        </>
    )
}

export default ModuleSchedule
